import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/lib/api";
import { Card, ChipRow } from "@/components/kit";
import { C, eur } from "@/lib/format";

const MONTHS = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
const H = 140;

function Bar({ value, max, color }) {
  const h = value ? Math.max(4, (value / max) * (H - 16)) : 2;
  return (
    <div className="flex flex-col items-center justify-end" style={{ height: H }}>
      {value ? <span className="text-[10px]" style={{ color }}>{value}</span> : null}
      <div className="w-2 rounded-md transition-all duration-500 md:w-3" style={{ height: h, backgroundColor: value ? color : C.border }} />
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: color }} />
      <span className="t-caption">{label}</span>
    </span>
  );
}

export function MonthlyChart({ path, testID }) {
  const [range, setRange] = useState("6");
  const q = useQuery({ queryKey: ["monthly", path], queryFn: () => api(path) });
  const data = (q.data ?? []).slice(range === "6" ? -6 : -12);
  const max = Math.max(1, ...data.map((d) => Math.max(d.recommendations, d.signed)));
  const totals = data.reduce((a, d) => ({ r: a.r + d.recommendations, s: a.s + d.signed, c: a.c + d.commissions }), { r: 0, s: 0, c: 0 });
  return (
    <Card testID={testID} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="t-h3">Évolution mensuelle</h2>
          <p className="t-caption" data-testid={`${testID}-totals`}>
            {totals.r} recommandation(s) · {totals.s} contrat(s) signé(s) · {eur(totals.c)} de commissions
          </p>
        </div>
        <ChipRow options={[{ key: "6", label: "6 mois" }, { key: "12", label: "12 mois" }]} value={range} onChange={setRange} testIDPrefix={`${testID}-range`} className="!py-0" />
      </div>
      {q.data && totals.r === 0 && totals.s === 0 ? (
        <p className="t-caption" data-testid={`${testID}-empty`}>
          Aucune activité sur la période : les recommandations et contrats apparaîtront ici mois par mois.
        </p>
      ) : null}
      <div className={`flex items-end ${range === "6" ? "gap-3" : "gap-1"}`} style={{ height: H + 24 }}>
        {data.map((d) => (
          <div key={d.month} data-testid={`${testID}-month-${d.month}`} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex items-end gap-0.5" style={{ height: H }}>
              <Bar value={d.recommendations} max={max} color={C.brand} />
              <Bar value={d.signed} max={max} color={C.success} />
            </div>
            <span className="t-caption truncate !text-[10px]">{MONTHS[Number(d.month.slice(5)) - 1]}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <Legend color={C.brand} label="Recommandations" />
        <Legend color={C.success} label="Contrats signés" />
      </div>
    </Card>
  );
}
